import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listEvents, getEvent } from "@/lib/api";
import { championLeaderboard, initials, withDisplayNames } from "@/lib/scoring";
import { ChevronLeft, Crown, Trophy } from "lucide-react";

export default function SeasonStandings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tripCount, setTripCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Get all my trips
        const trips = await listEvents({ mine: true });
        setTripCount(trips.length);
        // Load full events + aggregate championLeaderboard
        const totals = {};
        await Promise.all(trips.map(async (t) => {
          try {
            const ev = await getEvent(t.id);
            const lb = championLeaderboard(withDisplayNames(ev));
            for (const r of lb) {
              const key = (r.player.profileId || r.player.name || "").trim().toLowerCase();
              if (!key) continue;
              if (!totals[key]) totals[key] = { name: r.player.name, championPoints: 0, dayWins: 0, trips: 0 };
              totals[key].championPoints += r.championPoints;
              totals[key].dayWins += r.dayWins;
              if (r.championPoints > 0) totals[key].trips += 1;
            }
          } catch (e) { console.warn("Skipping trip in season rollup:", t.id, e); }
        }));
        const list = Object.values(totals).sort((a, b) => b.championPoints - a.championPoints || b.dayWins - a.dayWins);
        setRows(list);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="max-w-xl mx-auto px-5 pt-8 pb-16 tga-fade">
      <button data-testid="season-back-btn" onClick={() => navigate("/")} className="ghost-btn rounded-lg px-3 py-2 text-xs mb-4 inline-flex items-center gap-1">
        <ChevronLeft size={14} /> Home
      </button>
      <div className="text-center mb-6">
        <div className="crest crest-lg mx-auto mb-4"><Trophy size={32} /></div>
        <h1 className="font-display text-4xl font-semibold text-[color:var(--brass-bright)]">Order of Merit</h1>
        <p className="mt-2 text-[color:var(--muted-green)] italic font-display">Champion Points rolled up across every trip you own.</p>
      </div>

      <div className="tga-card">
        {loading ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Adding up scores across trips…</p>
        ) : rows.length === 0 ? (
          <p className="helptext text-[color:var(--text-dark)]/70">Finalize a day on at least one of your trips to see the season order of merit.</p>
        ) : (
          <>
            <p className="text-[11px] text-[color:var(--brass-dark)] mb-2 font-mono-metrics uppercase tracking-wide">Across {tripCount} trip{tripCount === 1 ? "" : "s"}</p>
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Trips</th>
                  <th className="py-1 text-right">Wins</th>
                  <th className="py-1 text-right">CP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.name)}</div>
                      <span className="font-sans">{r.name}</span>
                      {i === 0 && r.championPoints > 0 && <Crown size={14} className="text-[color:var(--brass)]" />}
                      {r.dayWins > 0 && (
                        <span className="text-[10px] font-mono-metrics font-bold bg-[rgba(197,160,89,0.15)] text-[color:var(--brass-dark)] rounded-full px-1.5 py-0.5 border border-[rgba(197,160,89,0.4)]">
                          🏆 {r.dayWins}
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right">{r.trips}</td>
                    <td className="py-2 text-right">{r.dayWins}</td>
                    <td className="py-2 text-right font-bold">{r.championPoints.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="helptext text-[color:var(--text-dark)]/60 mt-3 text-xs">Names are matched across trips case-insensitively — keep names consistent for perfect rollups.</p>
          </>
        )}
      </div>
    </div>
  );
}
